/* eslint-disable react/forbid-prop-types */
import React, { createContext, useContext, useEffect, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import * as Sentry from '@sentry/react';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';

import { Alert, Button, Drawer, MultiSelect, Text, Badge, Select } from '@mantine/core';

import {
  CUBOH_MENU_CHILD_MAP,
  CUBOH_MENU_PARENT_MAP,
  MENU_OBJECT_TYPES,
  TYPE_TO_READABLE_STRING_MAP,
} from 'constants/menuManagement';

import { CUBOH_SUPPORT_EMAIL } from 'constants/cuboh';

import {
  formatNameWithPrice,
  getItemsFromReadOnlyMenu,
  getItemsFromReadOnlyMenuByTargetParent,
} from 'helpers/menuManagement';

import { useAuth } from 'hooks/useAuth';
import { useMenuManagement } from 'hooks/useMenuManagement';

import DockedDrawerFormContent from 'primitives/DockedDrawer/DockedDrawerFormContent';
import { BellIconMd } from '@chownow/pantry/icons';



const ALERT_MESSAGES = {
  EMPTY_CATEGORY: 'This Category contains no items. Please execute a POS Refresh and try again.',
  DEFAULT: 'No options available',
};
dayjs.extend(timezone);

const SelectModalContext = createContext();

function SelectItemFromPosDrawer({
  parentRecord,
  itemType,
  selectedOptions,
  setSelectedOptions,
  opened,
  closeAddItemFromPosDrawer,
  openAddItemDisplayDrawer,
  openAddTypeChoiceDrawer,
  dropdownOptions,
  setDropdownOptions,
  isLoading,
  setIsLoading,
  setMessage,
  children,
}) {
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);

  const { selectedTimezone } = useAuth();
  const { readOnlySyncData, posRefreshInProgress } = useMenuManagement();
  const handleCancel = useCallback(() => {
    setSelectedOptions({});
    closeAddItemFromPosDrawer();
  }, [setSelectedOptions, closeAddItemFromPosDrawer]);
  const handleContinue = useCallback(() => {
    closeAddItemFromPosDrawer();
    openAddItemDisplayDrawer();
  }, [closeAddItemFromPosDrawer, openAddItemDisplayDrawer]);

  const populateOptions = useCallback(
    async ({ optionsItemType, resetDropdownOptions, currentSelection = null }) => {
      if (dropdownOptions[optionsItemType]?.length && !resetDropdownOptions) return;
      setIsLoading(true);
      try {
        let response;
        if ([MENU_OBJECT_TYPES.categories, MENU_OBJECT_TYPES.items].includes(optionsItemType)) {
          // we only need to handle main items here
          // selected options only apply to choosing which category to add a main item from
          // a category will fall into the '0' default
          const parentItems = currentSelection
            ? currentSelection[CUBOH_MENU_PARENT_MAP[optionsItemType][0]]
            : selectedOptions[CUBOH_MENU_PARENT_MAP[optionsItemType][0]] || [{ uuid: '0' }];

          response = await getItemsFromReadOnlyMenu({
            record: parentRecord,
            itemType: optionsItemType,
            parentUuid: parentItems[0]?.uuid || '0',
          });
        } else {
          // other item levels are only added based on a matching parent
          // in the read-only menu, so we send the parent and let the backend do the work
          response = await getItemsFromReadOnlyMenuByTargetParent({
            record: parentRecord,
          });
          // format the backend response into the select options format
        }
        if (!response.length) {
          setMessage(
            posRefreshInProgress
              ? `Categories are not available for selection until the POS Sync is complete. If the Sync has been in progress for over 90 minutes, please contact ${CUBOH_SUPPORT_EMAIL} or try again.`
              : 'This Category contains no items. Please execute a POS Refresh and try again.',
          );
          return;
        }

        // if response is an empty array, set empty options and return early
        if (!response || response.length === 0) {
          setDropdownOptions({
            ...dropdownOptions,
            [optionsItemType]: [],
          });
          setIsLoading(false);
          return;
        }

        // Deduplicate items by UUID
        // This handles cases where the API returns duplicate items with the same UUID
        const dedupedItems = new Map();

        response.forEach(item => {
          if (!dedupedItems.has(item.uuid)) {
            dedupedItems.set(item.uuid, item);
          }
        });

        const options = Array.from(dedupedItems.values()).map(item => ({
          ...item,
          label: formatNameWithPrice(item),
          value: item?.uuid,
          key: item?.uuid,
        }));

        setDropdownOptions({
          ...dropdownOptions,
          [optionsItemType]: options,
        });
      } catch (errorResponse) {
        setMessage(errorResponse?.response?.data?.detail || 'Unknown error. Please try again.');
        Sentry.captureException(errorResponse);
      } finally {
        setIsLoading(false);
      }
    },
    [
      dropdownOptions,
      parentRecord,
      setDropdownOptions,
      setIsLoading,
      selectedOptions,
      setMessage,
      posRefreshInProgress,
    ],
  );

  useEffect(() => {
    if (!opened || hasAttemptedLoad) return;

    const loadOptions = async () => {
      setHasAttemptedLoad(true);
      // for the special case of adding main items, we first need to populate the categories
      // for everything else, we can just populate the options for their specific type
      await populateOptions({
        optionsItemType:
          itemType === MENU_OBJECT_TYPES.items ? MENU_OBJECT_TYPES.categories : itemType,
      });
    };

    loadOptions();
  }, [opened, itemType, hasAttemptedLoad, populateOptions]);

  const providerValue = useMemo(
    () => ({
      selectedOptions,
      setSelectedOptions,
      populateOptions,
      dropdownOptions,
      isLoading,
      itemTypeToAdd: itemType,
    }),
    [dropdownOptions, isLoading, itemType, populateOptions, selectedOptions, setSelectedOptions],
  );

  const mostRecentPOSSync = useMemo(() => {
    const latestSync = Object.values(readOnlySyncData).reduce((latest, current) => {
      if (current.lastSyncDate === null) {
        return latest;
      }
      const currentTimeStamp = dayjs(current.lastSyncDate);
      return currentTimeStamp.unix() > latest ? currentTimeStamp.unix() : latest;
    }, 0);
    if (latestSync !== 0) {
      return dayjs.unix(latestSync).tz(selectedTimezone).format('MM/DD/YYYY hh:mm a');
    }
    return null;
  }, [readOnlySyncData, selectedTimezone]);

  const renderButtons = () => (
    <>
      {itemType === MENU_OBJECT_TYPES.categories && (
        <Button
          variant="outline"
          onClick={() => {
            setSelectedOptions({});
            closeAddItemFromPosDrawer();
            openAddTypeChoiceDrawer();
          }}
        >
          Back
        </Button>
      )}
      <Button
        onClick={handleContinue}
        disabled={!selectedOptions[itemType]?.length}
        fullWidth={itemType !== MENU_OBJECT_TYPES.categories}
      >
        Next
      </Button>
    </>
  );

  return (
    <SelectModalContext.Provider value={providerValue}>
      <Drawer
        title={
          <>
            <div>
              Copy&nbsp;
              {TYPE_TO_READABLE_STRING_MAP[CUBOH_MENU_CHILD_MAP[parentRecord.itemType]] === 'Item'
                ? 'an'
                : 'a'}
              &nbsp;
              {TYPE_TO_READABLE_STRING_MAP[CUBOH_MENU_CHILD_MAP[parentRecord.itemType]]}
              &nbsp;from your POS
            </div>
            {mostRecentPOSSync && (
              <div>
                <Badge variant="blue">Latest POS Sync Date: {mostRecentPOSSync}</Badge>
              </div>
            )}
          </>
        }
        opened={opened}
        onClose={handleCancel}
        variant="withDock"
      >
        <DockedDrawerFormContent dockButtons={renderButtons()}>{children}</DockedDrawerFormContent>
      </Drawer>
    </SelectModalContext.Provider>
  );
}

SelectItemFromPosDrawer.propTypes = {
  parentRecord: PropTypes.object.isRequired,
  itemType: PropTypes.string.isRequired,
  selectedOptions: PropTypes.object.isRequired,
  setSelectedOptions: PropTypes.func.isRequired,
  opened: PropTypes.bool.isRequired,
  closeAddItemFromPosDrawer: PropTypes.func.isRequired,
  openAddItemDisplayDrawer: PropTypes.func.isRequired,
  openAddTypeChoiceDrawer: PropTypes.func.isRequired,
  dropdownOptions: PropTypes.object.isRequired,
  setDropdownOptions: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  setMessage: PropTypes.func.isRequired,
  children: PropTypes.node,
};

function ResyncSelect({ selectType, disabled, hidden, multipleAllowed, message }) {
  const {
    selectedOptions,
    setSelectedOptions,
    populateOptions,
    dropdownOptions,
    isLoading,
    itemTypeToAdd,
  } = useContext(SelectModalContext);

  if (hidden) {
    return null;
  }

  // Only use single select when we're adding items and this is the category selector
  const shouldUseSingleSelect =
    itemTypeToAdd === MENU_OBJECT_TYPES.items && selectType === MENU_OBJECT_TYPES.categories;

  const cascadeReset = useCallback((resetType, currentSelectedOptions) => {
    const newSelectedOptions = { ...currentSelectedOptions };
    newSelectedOptions[resetType] = [];
    if (CUBOH_MENU_CHILD_MAP[resetType] && resetType !== MENU_OBJECT_TYPES.modifiers) {
      cascadeReset(CUBOH_MENU_CHILD_MAP[resetType], newSelectedOptions);
    }
    return newSelectedOptions;
  }, []);

  const handleChange = useCallback(
    async values => {
      let updatedSelectedOptions = { ...selectedOptions };
      if (!multipleAllowed) {
        updatedSelectedOptions = cascadeReset(selectType, updatedSelectedOptions);
      }

      const valueArray = typeof values === 'string' ? [values] : values;

      updatedSelectedOptions[selectType] = valueArray.map(uuid => ({
        name: dropdownOptions[selectType].find(item => item.value === uuid)?.label,
        uuid,
        itemType: selectType,
        isDeleted: false,
      }));

      // Clear selected items when changing category selection
      if (selectType === MENU_OBJECT_TYPES.categories) {
        updatedSelectedOptions[MENU_OBJECT_TYPES.items] = [];
      }

      setSelectedOptions(updatedSelectedOptions);

      // load items for the selected category
      const shouldLoadItemsForCategory =
        selectType === MENU_OBJECT_TYPES.categories && itemTypeToAdd === MENU_OBJECT_TYPES.items;

      // when selecting a parent item (except categories and modifiers), load its sub-items
      const shouldLoadSubItemsForParent =
        itemTypeToAdd !== MENU_OBJECT_TYPES.categories &&
        selectType !== MENU_OBJECT_TYPES.modifiers;

      if (shouldLoadItemsForCategory) {
        await populateOptions({
          optionsItemType: MENU_OBJECT_TYPES.items,
          currentSelection: updatedSelectedOptions,
          resetDropdownOptions: true,
        });
      } else if (shouldLoadSubItemsForParent) {
        await populateOptions({
          optionsItemType: CUBOH_MENU_CHILD_MAP[selectType],
          currentSelection: updatedSelectedOptions,
          resetDropdownOptions: true,
        });
      }
      // this is for mod groups & modifiers
    },
    [
      cascadeReset,
      dropdownOptions,
      itemTypeToAdd,
      multipleAllowed,
      populateOptions,
      selectType,
      selectedOptions,
      setSelectedOptions,
      shouldUseSingleSelect,
    ],
  );

  const formatValues = useCallback(
    type => {
      const selectedValues =
        selectedOptions[type]
          ?.filter(selected => !selected.isDeleted)
          .map(selected => selected?.uuid) || [];

      return shouldUseSingleSelect ? selectedValues[0] : selectedValues;
    },
    [selectedOptions, shouldUseSingleSelect],
  );

  const optionsNotInitialized = !dropdownOptions[selectType];
  const optionsEmpty = dropdownOptions[selectType]?.length === 0;
  const hasSelectedCategories = selectedOptions[MENU_OBJECT_TYPES.categories]?.length > 0;
  const isEmptyCategoryWithItems =
    optionsEmpty && selectType === MENU_OBJECT_TYPES.items && hasSelectedCategories;

  // Only show alert for items if a category is selected
  const shouldShowAlert =
    !isLoading &&
    (optionsNotInitialized || optionsEmpty) &&
    (selectType !== MENU_OBJECT_TYPES.items || hasSelectedCategories);

  if (shouldShowAlert) {
    const alertMessage = isEmptyCategoryWithItems
      ? ALERT_MESSAGES.EMPTY_CATEGORY
      : message || ALERT_MESSAGES.DEFAULT;

    return (
      <>
        <Text mb="xs">{TYPE_TO_READABLE_STRING_MAP[selectType]}</Text>
        <Alert icon={<BellIconMd />} variant="blue" mb="lg">
          {alertMessage}
        </Alert>
      </>
    );
  }

  const SelectComponent = shouldUseSingleSelect ? Select : MultiSelect;

  return (
    <SelectComponent
      data={dropdownOptions[selectType] || []}
      disabled={disabled || (!isLoading && !dropdownOptions[selectType])}
      value={formatValues(selectType)}
      placeholder={
        isLoading
          ? 'Loading options...'
          : `Select ${TYPE_TO_READABLE_STRING_MAP[selectType].toLowerCase()}`
      }
      label={`${TYPE_TO_READABLE_STRING_MAP[selectType]} *`}
      onChange={handleChange}
      clearable={!shouldUseSingleSelect && multipleAllowed}
      mb="lg"
    />
  );
}

ResyncSelect.propTypes = {
  selectType: PropTypes.string.isRequired,
  hidden: PropTypes.bool.isRequired,
  multipleAllowed: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  message: PropTypes.string.isRequired,
};

SelectItemFromPosDrawer.ResyncSelect = ResyncSelect;

export default SelectItemFromPosDrawer;
